import React, { useState, useMemo } from 'react';
// --- FIX 1: Changed import path to be local ---
import { FaHistory, FaRupeeSign } from 'react-icons/fa';

// Helper to format numbers as Indian Rupee (e.g., ₹1,20,000)
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);
};

/**
 * This is the core logic function.
 * @param {array} navData - The array of NAV data, sorted latest-first.
 * @param {number} monthlyAmount - The SIP amount (e.g., 5000).
 * @param {number} years - The investment period (e.g., 3).
 * @returns {object} - The calculated results.
 */
const calculateHistoricalSip = (navData, monthlyAmount, years) => {
  if (!navData || navData.length === 0) {
    return { totalInvested: 0, currentValue: 0, totalGain: 0, gainPercent: 0 };
  }

  const sipDay = 1; // We'll assume SIP on the 1st of each month
  const totalMonths = years * 12;
  const latestNav = parseFloat(navData[0].nav);
  let totalUnits = 0;
  let totalInvested = 0;

  // --- FIX 2: Correctly parse the "dd-mm-yyyy" date ---
  const [latestDay, latestMonth, latestYear] = navData[0].date.split('-').map(Number);
  const today = new Date(latestYear, latestMonth - 1, latestDay);
  // ---------------------------------------------------

  for (let i = 0; i < totalMonths; i++) {
    // Go back month by month
    const sipDate = new Date(today.getFullYear(), today.getMonth() - i, sipDay);

    // Find the NAV for that date.
    // We must find the *first available trading day* on or *after* the SIP date.
    // The data is sorted new-to-old, so we find the *last* item that is *before* our sipDate.
    
    // We reverse the data to search from old-to-new
    const navs = [...navData].reverse(); 
    
    const navEntry = navs.find(nav => {
      const [day, month, year] = nav.date.split('-').map(Number);
      const navDate = new Date(year, month - 1, day);
      return navDate >= sipDate;
    });

    if (navEntry) {
      const navValue = parseFloat(navEntry.nav);
      const unitsBought = monthlyAmount / navValue;
      totalUnits += unitsBought;
      totalInvested += monthlyAmount;
    }
  }

  // Handle case where no investments were made (e.g., fund is too new)
  if (totalInvested === 0) {
    return { totalInvested: 0, currentValue: 0, totalGain: 0, gainPercent: 0 };
  }

  const currentValue = totalUnits * latestNav;
  const totalGain = currentValue - totalInvested;
  const gainPercent = (totalGain / totalInvested) * 100;

  return {
    totalInvested: Math.round(totalInvested),
    currentValue: Math.round(currentValue),
    totalGain: Math.round(totalGain),
    gainPercent: gainPercent.toFixed(2), // e.g., "15.20"
  };
};


function HistoricalSipCalculator({ navData }) {
  const [monthlyAmount, setMonthlyAmount] = useState(5000);
  const [years, setYears] = useState(3); // Default to 3 years

  // Re-calculate only when one of the inputs changes
  const sipResults = useMemo(() => {
    return calculateHistoricalSip(navData, monthlyAmount, years);
  }, [navData, monthlyAmount, years]);

  // Helper for the stacked bar
  const totalValue = sipResults.currentValue || 1; // Avoid divide by zero
  const investedPercent = (sipResults.totalInvested / totalValue) * 100;
  const gainsPercent = (sipResults.totalGain / totalValue) * 100;
  const isProfit = sipResults.totalGain >= 0;

  // Don't render anything if the fund doesn't have enough data for a 1-year calculation
  // We check for 252 (1 year) because that's the minimum useful period
  if (!navData || navData.length < 252) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 mt-12">
        <h2 className="text-2xl font-bold text-gray-500 text-center">
          Historical SIP data is not available for this fund.
        </h2>
        <p className="text-center text-gray-400">The fund may be less than 1 year old.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 mt-12">
      <div className="flex items-center mb-6">
        <FaHistory className="h-8 w-8 text-blue-600 mr-3" />
        <h2 className="text-3xl font-bold text-gray-900">Historical SIP Performance</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* --- Column 1: Inputs --- */}
        <div className="space-y-6">
          {/* Monthly Investment */}
          <div>
            <label className="text-lg font-medium text-gray-700">Monthly SIP Amount</label>
            <div className="relative mt-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <FaRupeeSign />
              </span>
              <input
                type="number"
                step="500"
                min="500"
                value={monthlyAmount}
                onChange={(e) => setMonthlyAmount(Number(e.target.value))}
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-lg font-bold"
              />
            </div>
          </div>

          {/* Investment Period */}
          <div>
            <label className="text-lg font-medium text-gray-700">Investment Period</label>
            <select
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
              className="w-full mt-2 p-3 rounded-lg bg-white border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-lg font-bold"
            >
              <option value="1">1 Year</option>
              <option value="3">3 Years</option>
              <option value="5">5 Years</option>
              {/* Note: We can only go as far back as the fund's data allows. This logic assumes data exists. */}
            </select>
          </div>
        </div>

        {/* --- Column 2: Results --- */}
        <div className="bg-blue-50 rounded-xl p-6 flex flex-col justify-center">
          <div className="text-center">
           <p className="text-lg text-gray-600">If you had invested {formatCurrency(monthlyAmount)}/month for {years} {years > 1 ? 'years' : 'year'}:</p>
          </div>

          <div className="w-full flex justify-between text-lg mt-6">
            <div className="text-left">
              <p className="text-sm text-gray-500">Total Invested</p>
              <p className="font-bold text-gray-800 text-2xl">{formatCurrency(sipResults.totalInvested)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Current Value</p>
              <p className="font-bold text-blue-600 text-2xl">{formatCurrency(sipResults.currentValue)}</p>
            </div>
          </div>
          
          {/* Stacked Bar Chart */}
          <div className="w-full h-8 flex rounded-lg overflow-hidden my-4">
            <div
              className="bg-gray-400 h-full"
              style={{ width: `${isProfit ? investedPercent : 100}%` }}
              title={`Invested: ${formatCurrency(sipResults.totalInvested)}`}
            />
            {isProfit && (
              <div
                className="bg-blue-600 h-full"
                style={{ width: `${gainsPercent}%` }}
                title={`Gains: ${formatCurrency(sipResults.totalGain)}`}
              />
            )}
          </div>
          
          <div className="text-center">
            <p className="text-lg text-gray-600">Total {isProfit ? 'Gain' : 'Loss'}</p>
            <p className={`text-4xl font-extrabold my-1 ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(sipResults.totalGain)}
            </p>
            <p className={`text-lg font-medium ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
              ({sipResults.gainPercent}%)
            </p>
          </div>
          <p className="text-xs text-gray-400 text-center mt-4">
            *Past performance is not indicative of future returns. Calculations are based on historical NAVs for the 1st of each month.
          </p>
        </div>
      </div>
    </div>
  );
}

export default HistoricalSipCalculator;